// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Icon from "@insite/mobius/Icon";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import CubeLine from "@store/Library/Components/Toolkit/Icons/Cube/CubeLine";
import SelectInline, { SelectInlineStyles } from "@store/Library/Components/Toolkit/SelectInline";
import { IconPresentationProps } from "@store/MobiusOverrides/Icon/Icon";
import { menuSecondaryNavigationItemStyles } from "@store/Themes/common/mixins/menu";
import { transitionHoverStyles } from "@store/Themes/common/mixins/transition";
import setCustomerCatalog from "@store/Framework/Store/Context/Handlers/SetCustomerCatalog";
import { CustomerCatalog } from "@store/Framework/Types/ApiModels";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { iconScalableStyles } from "@toolkit/Themes/common/mixins";
import React, { useMemo } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const iconSize = pxToRem(18);
const iconOffset = pxToRem(0);

const enum fields {
    showIcon = "showIcon",
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    return {
        customerCatalogs: JSON.parse(session?.properties?.catalogs || "[]") as CustomerCatalog[],
        currentCustomerCatalog: session?.properties?.currentCatalog,
    };
};

const mapDispatchToProps = {
    setCustomerCatalog,
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showIcon]: boolean;
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CustomerCatalogSelectionStyles {
    catalogIcon?: IconPresentationProps;
    customerCatalogSelectionWrapper?: InjectableCss;
    selectInline?: SelectInlineStyles;
}

export const customerCatalogSelectionStyles: CustomerCatalogSelectionStyles = {
    catalogIcon: {
        src: CubeLine,
        color: "inherit",
        css: css`
            ${iconScalableStyles(iconSize, false)}
            margin-right: ${x => x.theme.design.menu.secondaryNavigation.iconSpacing};
            position: absolute;
        `,
    },
    customerCatalogSelectionWrapper: {
        css: css`
            ${menuSecondaryNavigationItemStyles()}
            display: flex;
            align-items: center;

            &:hover {
                color: ${getColor("menu.secondaryNavigation.hover")};
                text-decoration: underline;
                transition: ${transitionHoverStyles()};
            }
        `,
    },
    selectInline: {
        select: {
            cssOverrides: {
                inputSelect: css`
                    font-weight: inherit;
                    line-height: inherit;
                    cursor: pointer;

                    &:hover {
                        text-decoration: underline;
                    }
                `,
            },
        },
    },
};

export const CustomerCatalogSelection = ({
    fields,
    customerCatalogs,
    currentCustomerCatalog,
    setCustomerCatalog,
}: Props) => {
    const selectInlineStyles = useMemo(() => {
        if (fields.showIcon) {
            return mergeProps<SelectInlineStyles>(customerCatalogSelectionStyles.selectInline, {
                select: {
                    cssOverrides: {
                        inputSelect: css`
                            ${({ theme }) => {
                                const iconSpacing = theme.design.menu.secondaryNavigation.iconSpacing;
                                return `padding-left: calc(${iconSize} + ${iconSpacing} - (${iconOffset} * 2)) !important;`;
                            }}
                        `,
                    },
                },
            });
        }

        return customerCatalogSelectionStyles.selectInline;
    }, [customerCatalogSelectionStyles, fields.showIcon]);

    if (!customerCatalogs || customerCatalogs.length <= 1) {
        return null;
    }

    const menuId = "customerCatalogSelection";
    return (
        <StyledWrapper {...customerCatalogSelectionStyles.customerCatalogSelectionWrapper}>
            {fields.showIcon && <Icon {...customerCatalogSelectionStyles.catalogIcon} />}
            <VisuallyHidden as="label" htmlFor={menuId} id={`${menuId}-label`}>
                {translate("Catalog")}
            </VisuallyHidden>
            <SelectInline
                extendedStyles={selectInlineStyles}
                uid={menuId}
                onChange={event => setCustomerCatalog({ customerCatalogId: event.currentTarget.value })}
                value={currentCustomerCatalog}
                hasLabel
            >
                {customerCatalogs.map(catalog => (
                    <option value={catalog.id} key={catalog.id}>
                        {catalog.displayName}
                    </option>
                ))}
            </SelectInline>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CustomerCatalogSelection),
    definition: {
        displayName: "Customer Catalog Selection",
        icon: "gear",
        fieldDefinitions: [
            {
                name: fields.showIcon,
                displayName: "Show Icon",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
